import { AxiosInstance } from "axios";
import { useEffect, useState } from "react";
import { URL } from "../libraries/http/url";
import { IItemNumber } from "../types/data.interface";
import { PageableDTO } from "../types/pageable";
import useAxios from "../utils/useAxios";
import { Predicates } from "../libraries/predicates/predicates";
import useService from "./useService";
import {
  itemNumberDTO,
  itemNumbersDTO,
} from "../routes/mpn/helpers/getItemNumber";
// import { mapItemNumberCompositionDTOToModel } from "helpers/itemnumber-composition.helper";

type SearchParams = {
  category?: number;
  sub_category?: number;
  product_line?: number;
  global_product?: number;
  search?: string;
  isPisPage?: boolean;
  active_global_tcc?: boolean;
  production_lifecycle_status?: string;
  sales_lifecycle_status?: string;
  id?: any;
};

type ItemNumberResponse = PageableDTO<IItemNumber[]> | IItemNumber[];

export const fetchItemNumber = async ({
  product_line,
  sub_category,
  category,
  global_product,
  search,
  isPisPage,
  active_global_tcc,
  production_lifecycle_status,
  sales_lifecycle_status,
  axios,
}: SearchParams & { axios: AxiosInstance }) => {
  try {
    const searchParams = URL.createSearchParams({
      global_product,
      global_product__sub_category__parent: category,
      global_product__sub_category: sub_category,
      global_product__product_line: product_line,
      search,
      page: isPisPage ? "pis" : "",
      active_global_tcc,
      production_lifecycle_status,
      sales_lifecycle_status,
    });

    const response = await axios.get<ItemNumberResponse>(
      `/tccs/?limit=75&${searchParams.toString()}`,
    );

    return itemNumberDTO(Predicates.parsePageableReponseToType(response));
  } catch (err) {
    console.error(err);
  }
  return [];
};

export const fetchItemNumberValid = async ({
  product_line,
  sub_category,
  category,
  global_product,
  search,
  isPisPage,
  active_global_tcc,
  production_lifecycle_status,
  sales_lifecycle_status,
  axios,
}: SearchParams & { axios: AxiosInstance }) => {
  try {
    const searchParams = URL.createSearchParams({
      global_product,
      global_product__sub_category__parent: category,
      global_product__sub_category: sub_category,
      global_product__product_line: product_line,
      search,
      page: isPisPage ? "pis" : "",
      active_global_tcc,
      production_lifecycle_status,
      sales_lifecycle_status,

      exclude_zero_pch: "1",
    });

    const response = await axios.get(`/tccs-search/?limit=75&${searchParams.toString()}`);
    return Predicates.parsePageableReponseToType(response);
  } catch (err) {
    console.error(err);
  }
  return [];
};

export const fetchItemnumberById = async ({
  id,
  axios,
}: {
  id: string;
  axios: AxiosInstance;
}) => {
  try {
    const response = await axios.get<IItemNumber>(`/tccs/${id}/`);
    return itemNumberDTO(Predicates.parsePageableReponseToType(response));

  } catch (err) {
    console.error(err);
  }
  return null;
};


export default function useItemNumber({
  global_product,
  product_line,
  sub_category,
  category,
  isPisPage,
  active_global_tcc,
  production_lifecycle_status,
  sales_lifecycle_status,
  id,
}: SearchParams) {
  const axios = useAxios();
  const [data, setData] = useState<IItemNumber[] | IItemNumber|null>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      if (id) {
        const response = await fetchItemnumberById({ axios, id });

        setData(response);
        setIsLoading(false);
      }
      else{
        const response = await fetchItemNumber({
          product_line,
          sub_category,
          category,
          global_product,
          isPisPage,
          active_global_tcc,
          production_lifecycle_status,
          sales_lifecycle_status,
          axios,
        });

        setData(response);
        setIsLoading(false);
      }
    };

    getData();
  }, [
    global_product,
    product_line,
    sub_category,
    category,
    isPisPage,
    active_global_tcc,
    production_lifecycle_status,
    sales_lifecycle_status,
  ]);

  return { data, isLoading };
}

export function useItemNumberService({
  global_product,
  product_line,
  sub_category,
  category,
}: SearchParams) {
  const searchParams = URL.createSearchParams({
    global_product,
    product_line,
    sub_category,
    category,
  });
  const { data, error, fetch, loading } = useService<ItemNumberResponse>(
    `/tccs/?${searchParams.toString()}`,
  );

  useEffect(() => {
    fetch();
  }, []);

  return {
    data: Predicates.isNotNullAndNotUndefined(data)
      ? Predicates.parsePageableReponseToType(data)
      : undefined,
    error,
    loading,
  };
}
